/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { sort, setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PageType from '@linc-technologies/linced-common/enums/page-type';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { templatedPostTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { processTemplate } from '@linc-technologies/linced-common/utils/post-templates';
import config from 'linced-admin/config/environment';

export default class PostTemplatesController extends Controller {
  @service
  router;

  @action
  setBulkApplyTemplate(value) {
    this.bulkApplyTemplate = value;
  }

  @action
  setPageType(value) {
    this.pageType = value;
  }

  queryParams = [{ pageType: 'type' }, 'jurisdiction'];

  // Services
  @service
  fetch;

  @service
  store;

  @service
  toaster;

  PageType = PageType;
  @tracked
  pageType = PageType.STUDENT;
  Jurisdiction = Jurisdiction;
  @tracked jurisdiction = null;

  get selectedJurisdiction() {
    if (isEmpty(this.jurisdiction)) {
      return undefined;
    }
    let jurisdiction = parseInt(this.jurisdiction, 10);
    return Jurisdiction.values.find((j) => j.value === jurisdiction);
  }

  sortOrder = Object.freeze(['sequence', 'name']);

  @sort('model.postTemplates', 'sortOrder')
  sortedPostTemplates;

  @tracked
  postTemplate = null;
  @tracked
  postTemplateDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked previewDialogOpen = false;
  @tracked editableRoot = false;

  get previewEditorOptions() {
    return templatedPostTinyMCEConfig(this.editableRoot);
  }

  @tracked templateContent = '';

  get previewContent() {
    if (!this.previewDialogOpen) {
      // Don't bother generating preview content if we're not previewing.
      return '';
    }
    return processTemplate(this.templateContent, {
      preview: true,
    });
  }

  // Bulk Apply dialog.
  bulkApplyDialogOpen = false;

  @tracked
  bulkApplyInProgress = false;
  @tracked
  bulkApplyTemplate = null;
  @tracked
  bulkApplyGroup = null;
  @tracked
  bulkApplyExclusions = [];
  @tracked bulkApplyProcessed = 0;

  @setDiff('bulkApplyGroup.schoolsArray', 'bulkApplyExclusions')
  remainingBulkApplySchools;

  @tracked
  description;

  @tracked
  name;

  @computed('bulkApplyGroup.schools.[]', 'bulkApplyProcessed')
  get bulkApplyProgress() {
    if (isEmpty(this.bulkApplyGroup.schools)) {
      return 100;
    }
    if (this.bulkApplyProcessed < 1) {
      return 0;
    }
    return (this.bulkApplyProcessed / this.bulkApplyGroup.schools.length) * 100;
  }

  @action
  noop() {}

  @action
  setJurisdictionFilter(jurisdiction) {
    if (isEmpty(jurisdiction)) {
      this.jurisdiction = null;
      return;
    }
    this.jurisdiction = jurisdiction.value;
  }

  @action
  edit(postTemplate) {
    postTemplate.reload().then((updated) => {
      this.postTemplateDialogOpen = true;
      this.postTemplate = updated;
    });
  }

  @action
  delete() {}

  @action
  addPostTemplate() {
    this.resetForm();
    this.postTemplateDialogOpen = true;
  }

  @action
  savePostTemplate(props) {
    this.saveInProgress = true;

    let record;
    if (isEmpty(this.postTemplate)) {
      record = this.store.createRecord('posts/post-template', {});
    } else {
      record = this.postTemplate;
    }

    record.setProperties(props);

    record
      .save()
      .then(() => {
        this.router.refresh('post-templates');
        this.postTemplateDialogOpen = false;
        this.toaster.success('Post Template saved successfully.');
      })
      .catch(() => {
        this.toaster.error('Error saving Post Template!');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  preview(templateContent, editableRoot) {
    this.templateContent = templateContent;
    this.previewDialogOpen = true;
    this.editableRoot = editableRoot;
  }

  @action
  toggleBulkApplyDialog() {
    this.toggleProperty('bulkApplyDialogOpen');
    if (!this.bulkApplyDialogOpen) {
      this.bulkApplyInProgress = false;
      this.bulkApplyTemplate = null;
      this.bulkApplyGroup = null;
      this.bulkApplyExclusions = [];
      this.bulkApplyProcessed = 0;
    }
  }

  @action
  addBulkApplyExclusion(school) {
    this.bulkApplyExclusions = [...this.bulkApplyExclusions, school];
  }

  @action
  removeBulkApplyExclusion(school) {
    this.bulkApplyExclusions = this.bulkApplyExclusions.filter(
      (s) => s !== school,
    );
  }

  @action
  setBulkApplyGroup(group) {
    this.bulkApplyGroup = group;
    this.bulkApplyExclusions = [];
  }

  @action
  bulkApply() {
    this.bulkApplyInProgress = true;
    this.bulkApplyProcessed = 0;
    let excludedIds = mapBy(this.bulkApplyExclusions, 'id');
    let templateId = this.bulkApplyTemplate.id;
    this.bulkApplyGroup.schoolsArray.reduce((chain, school) => {
      return chain.then(() => {
        if (excludedIds.includes(school.id)) {
          this.incrementProperty('bulkApplyProcessed');
          return Promise.resolve();
        }
        return this.fetch
          .post(
            `${config.APP.apiHost}/posts/${config.APP.apiPath}/templates/posttemplates/${templateId}/:apply`,
            {
              data: JSON.stringify({ schoolId: school.id }),
              headers: { 'X-Tenant-Id': school.id },
            },
          )
          .then((data) => {
            // Set the new Post Template's sequence to 999 to put it at the
            // end of the list, since the sequence won't necessarily align
            // with the school's existing Post Templates.
            data.postTemplate.sequence = 999;
            return this.fetch.put(
              `${config.APP.apiHost}/posts/${config.APP.apiPath}/posttemplates/${data.postTemplate.id}`,
              { data },
            );
          })
          .then(() => this.incrementProperty('bulkApplyProcessed'));
      });
    }, Promise.resolve());
  }

  resetForm() {
    this.name = '';
    this.description = '';
    this.postTemplate = '';
    this.editableRoot = false;
  }
}
